import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

type Company = {
  id: number; name: string; vat_number: string | null; country: string | null;
  created_at: string; member_count: number; registration_count: number;
};

export default function AdminCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    api.get<Company[]>('/admin/companies').then(setCompanies).finally(() => setLoading(false));
  }, []);

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.country ?? '').toLowerCase().includes(search.toLowerCase())
  );

  async function deleteCompany(id: number, name: string) {
    if (!confirm(`Delete "${name}" and remove all its members? Registrations will be kept but unlinked from the company.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/companies/${id}`);
      setCompanies(p => p.filter(c => c.id !== id));
    } finally { setDeletingId(null); }
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Companies</h1>
        <span style={{ fontSize: 13, color: '#8f9b9b' }}>{companies.length} total</span>
      </div>

      <div className="mb-4">
        <input className="input" style={{ maxWidth: 320 }} placeholder="Search by name or country…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
        {loading ? (
          <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 560 }}>
              <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr style={{ textAlign: 'left' }}>
                  {['Company', 'Country', 'Members', 'Registrations', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ fontWeight: 600, color: '#0b0d0e' }}>{c.name}</p>
                      {c.vat_number && <p style={{ fontSize: 11, color: '#8f9b9b' }}>VAT: {c.vat_number}</p>}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{c.country ?? '—'}</td>
                    <td style={{ padding: '12px 16px', color: '#374151', fontWeight: 500 }}>{c.member_count}</td>
                    <td style={{ padding: '12px 16px', color: '#374151', fontWeight: 500 }}>{c.registration_count}</td>
                    <td style={{ padding: '12px 16px', color: '#8f9b9b', fontSize: 12 }}>{c.created_at.slice(0, 10)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => deleteCompany(c.id, c.name)}
                        disabled={deletingId === c.id}
                        style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        {deletingId === c.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#8f9b9b' }}>No companies found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
